import { PaginationRequest } from "src/app/models/common/paginationRequest";
import { Action, createReducer, on } from "@ngrx/store";

import * as VehiclesActionTypes from "../../actions/vehicles/vehicles.actions";
import * as PartiesActionTypes from "../../actions/parties/parties.actions";

import { CategoryDTO } from "src/app/models/Categories/categoryDTO";
import { PropertyDTO } from "src/app/models/Properties/propertyDTO";
import { CategoryElementDTO } from "src/app/models/Categories/categoryElementDTO";
import { VehicleExtendedDTO } from "src/app/models/Vehicles/VehicleExtendedDTO";
import { VehicleOdometerTypeDTO } from "src/app/models/Vehicles/VehicleOdometerTypeDTO";
import { VehicleConsumptionRuleDTO } from "src/app/models/Vehicles/VehicleConsumptionRuleDTO";
import { ColumnSorterInfo } from "src/app/models/custom-table/custom-table.model";

export interface VehiclesState {
  vehicleList: any;
  vehicle: VehicleExtendedDTO;
  created: boolean;
  isLoading: boolean;
  error: Error;
  totalRecords: number;
  pageReq: PaginationRequest;
  categoriesList: CategoryDTO[];
  propertiesList: PropertyDTO[];
  categories: CategoryDTO[];
  optionalCategories: CategoryDTO[];
  optionalProperties: PropertyDTO[];
  categoryElements: CategoryElementDTO[];
  properties: PropertyDTO[];
  brandList: any[];
  modelList: any[];
  versionList: any[];
  motorizationList: any[];
  customerList: any[];
  productGroupList: any[];
  odometerTypeList: VehicleOdometerTypeDTO[];
  consumptionRuleList: VehicleConsumptionRuleDTO[];
  cardList?: any;
  vehicleAverages?: any;
  vehicleTransactions?: any[];
  vehicleExcelList?: any[];
  totalVehicles?: number;
  vehicleTableConfig: ColumnSorterInfo[],
}

export const initialState: VehiclesState = {
  vehicleList: null,
  vehicle: null,
  created: false,
  isLoading: false,
  error: null,
  totalRecords: 0,
  pageReq: null,
  categoriesList: null,
  propertiesList: null,
  properties: null,
  optionalCategories: null,
  optionalProperties: null,
  categories: null,
  categoryElements: null,
  brandList: null,
  modelList: null,
  versionList: null,
  motorizationList: null,
  customerList: null,
  productGroupList: null,
  odometerTypeList: null,
  consumptionRuleList: null,
  cardList: null,
  vehicleAverages: null,
  vehicleTransactions: null,
  vehicleExcelList: null,
  totalVehicles: null,
  vehicleTableConfig: null,
};

const vehiclesReducer = createReducer(
  initialState,
  on(VehiclesActionTypes.resetVehiclesState, () => initialState),
  on(VehiclesActionTypes.setLoadingVehiclesStartState, (state) => {
    return {
      ...state,
      isLoading: true,
      error: null,
    };
  }),
  on(VehiclesActionTypes.setLoadingVehiclesEndState, (state) => {
    return {
      ...state,
      isLoading: false,
      error: null,
    };
  }),
  on(
    VehiclesActionTypes.getByVehicleCardsList,
    (state, { container, options }) =>
      ({
        ...state,
        error: null,
        container: container,
        options: options,
      } as VehiclesState)
  ),
  on(VehiclesActionTypes.loadByVehicleCardsList, (state, { response }) => {
    let currentOffset = state.pageReq ? state.pageReq.offset : 0;
    let totalRecords = Math.max(
      currentOffset + (response as any)?.result?.size,
      state.totalRecords
    );
    return {
      ...state,
      cardList: (response as any)?.result || [],
      error: null,
      totalRecords: totalRecords,
    };
  }),
  on(
    VehiclesActionTypes.getByTransactionsVehicleList,
    (state, { options }) =>
      ({
        ...state,
        error: null,
        options: options,
      } as VehiclesState)
  ),
  on(
    VehiclesActionTypes.loadByTransactionsVehicleList,
    (state, { response }) => {
      let currentOffset = state.pageReq ? state.pageReq.offset : 0;
      let totalRecords = Math.max(
        currentOffset + (response as any)?.result?.size,
        state?.totalRecords
      );
      return {
        ...state,
        vehicleTransactions: (response as any)?.result || [],
        error: null,
        totalRecords: totalRecords,
      };
    }
  ),
  on(VehiclesActionTypes.setVehicle, (state, { vehicle }) => ({
    ...state,
    vehicle: (vehicle as any).result,
    isLoading: false,
    error: null,
  })),
  on(VehiclesActionTypes.getVehicleList, (state, { options }) => ({
    ...state,
    options,
    isLoading: true,
  })),
  on(VehiclesActionTypes.loadVehicleList, (state, { response }) => ({
    ...state,
    vehicleList: (response as any)?.result?.items || undefined,
    error: null,
    isLoading: false,
    totalVehicles: (response as any)?.result?.size
      ? (response as any)?.result?.size
      : 0,
  })),
  on(VehiclesActionTypes.loadProductGroups, (state, { response }) => {
    return {
      ...state,
      productGroupList: (response as any)?.result?.items || [],
      error: null,
    };
  }),
  on(
    VehiclesActionTypes.loadVehicleCategoriesByUsage,
    (state, { response }) => {
      let currentOffset = state.pageReq ? state?.pageReq?.offset : 0;
      let totalRecords = Math.max(
        currentOffset + (response as any)?.result?.size,
        state.totalRecords
      );
      return {
        ...state,
        categories: (response as any)?.result?.items || [],
        error: null,
        totalRecords: totalRecords,
      };
    }
  ),
  on(
    VehiclesActionTypes.loadVehiclePropertiesByUsage,
    (state, { response }) => {
      let currentOffset = state.pageReq ? state?.pageReq?.offset : 0;
      let totalRecords = Math.max(
        currentOffset + (response as any)?.result?.size,
        state.totalRecords
      );
      return {
        ...state,
        properties: (response as any)?.result?.items || [],
        error: null,
        totalRecords: totalRecords,
      };
    }
  ),
  on(
    VehiclesActionTypes.loadVehicleCategoryElements,
    (state, { response }) => ({
      ...state,
      categoryElements: (response as any)?.result,
      error: null,
    })
  ),
  on(
    VehiclesActionTypes.loadVehicleOptionalCategoriesByUsage,
    (state, { response }) => {
      let currentOffset = state.pageReq ? state?.pageReq?.offset : 0;
      let totalRecords = Math.max(
        currentOffset + (response as any)?.result?.size,
        state?.totalRecords
      );
      return {
        ...state,
        optionalCategories: (response as any)?.result?.items || [],
        error: null,
        totalRecords: totalRecords,
      };
    }
  ),
  on(
    VehiclesActionTypes.loadVehicleOptionalPropertiesByUsage,
    (state, { response }) => {
      let currentOffset = state.pageReq ? state?.pageReq?.offset : 0;
      let totalRecords = Math.max(
        currentOffset + (response as any)?.result?.size,
        state.totalRecords
      );
      return {
        ...state,
        optionalProperties: (response as any)?.result?.items || [],
        error: null,
        totalRecords: totalRecords,
      };
    }
  ),
  on(
    VehiclesActionTypes.loadVehicleCategoriesForListByUsage,
    (state, { response }) => {
      let currentOffset = state.pageReq ? state.pageReq?.offset : 0;
      let totalRecords = Math.max(
        currentOffset + (response as any)?.result?.size,
        state?.totalRecords
      );
      return {
        ...state,
        categoriesList: (response as any)?.result?.items || [],
        error: null,
        totalRecords: totalRecords,
      };
    }
  ),
  on(
    VehiclesActionTypes.loadVehiclePropertiesForListByUsage,
    (state, { response }) => {
      let currentOffset = state.pageReq ? state.pageReq.offset : 0;
      let totalRecords = Math.max(
        currentOffset + (response as any)?.result?.size,
        state.totalRecords
      );
      return {
        ...state,
        propertiesList: (response as any)?.result?.items || [],
        error: null,
        totalRecords: totalRecords,
      };
    }
  ),
  on(
    VehiclesActionTypes.getBrands,
    (state, { pageReq }) =>
      ({
        ...state,
        error: null,
        pageReq: pageReq,
      } as VehiclesState)
  ),
  on(VehiclesActionTypes.loadBrands, (state, { response }) => {
    let currentOffset = state.pageReq ? state?.pageReq?.offset : 0;
    let totalRecords = Math.max(
      currentOffset + (response as any)?.result?.size,
      state.totalRecords
    );
    return {
      ...state,
      brandList: (response as any)?.result?.items || [],
      error: null,
      totalRecords: totalRecords,
    };
  }),
  on(
    VehiclesActionTypes.getModels,
    (state, { pageReq }) =>
      ({
        ...state,
        error: null,
        pageReq: pageReq,
      } as VehiclesState)
  ),
  on(VehiclesActionTypes.loadModels, (state, { response }) => {
    let currentOffset = state.pageReq ? state?.pageReq?.offset : 0;
    let totalRecords = Math.max(
      currentOffset + (response as any)?.result?.size,
      state?.totalRecords
    );
    return {
      ...state,
      modelList: (response as any)?.result?.items || [],
      error: null,
      totalRecords: totalRecords,
    };
  }),
  on(
    VehiclesActionTypes.getVersions,
    (state, { pageReq }) =>
      ({
        ...state,
        error: null,
        pageReq: pageReq,
      } as VehiclesState)
  ),
  on(VehiclesActionTypes.loadVersions, (state, { response }) => {
    let currentOffset = state.pageReq ? state?.pageReq?.offset : 0;
    let totalRecords = Math.max(
      currentOffset + (response as any)?.result?.size,
      state?.totalRecords
    );
    return {
      ...state,
      versionList: (response as any)?.result?.items || [],
      error: null,
      totalRecords: totalRecords,
    };
  }),
  on(
    VehiclesActionTypes.getMotorizations,
    (state, { pageReq }) =>
      ({
        ...state,
        error: null,
        pageReq: pageReq,
      } as VehiclesState)
  ),
  on(VehiclesActionTypes.loadMotorizations, (state, { response }) => {
    let currentOffset = state.pageReq ? state?.pageReq?.offset : 0;
    let totalRecords = Math.max(
      currentOffset + (response as any)?.result?.size,
      state?.totalRecords
    );
    return {
      ...state,
      motorizationList: (response as any).result.items || [],
      error: null,
      totalRecords: totalRecords,
    };
  }),
  on(VehiclesActionTypes.loadCustomerVehicleList, (state, { response }) => {
    return {
      ...state,
      customerList: (response as any)?.result
        ? (response as any)?.result
        : undefined,
      error: null,
    };
  }),
  on(VehiclesActionTypes.resetVehicle, (state) => {
    return {
      ...state,
      vehicle: null,
      error: null,
    };
  }),
  on(
    VehiclesActionTypes.getOdometerTypeList,
    (state, { pageReq }) =>
      ({
        ...state,
        error: null,
        pageReq: pageReq,
      } as VehiclesState)
  ),
  on(VehiclesActionTypes.loadOdometerTypeVehicleList, (state, { response }) => {
    return {
      ...state,
      odometerTypeList: (response as any)?.result?.items || [],
      error: null,
    };
  }),
  on(
    VehiclesActionTypes.getConsumptionRuleList,
    (state, { pageReq }) =>
      ({
        ...state,
        error: null,
        pageReq: pageReq,
      } as VehiclesState)
  ),
  on(
    VehiclesActionTypes.loadConsumptionRuleVehicleList,
    (state, { response }) => {
      return {
        ...state,
        consumptionRuleList: (response as any)?.result?.items || [],
        error: null,
      };
    }
  ),
  on(
    VehiclesActionTypes.getVehicleAverages,
    (state, { vehicleCode }) =>
      ({
        ...state,
        error: null,
        vehicleCode: vehicleCode,
      } as VehiclesState)
  ),
  on(VehiclesActionTypes.loadVehicleAverages, (state, { response }) => {
    return {
      ...state,
      vehicleAverages: (response as any)?.result?.items || [],
      error: null,
    };
  }),
  on(
    VehiclesActionTypes.getVehicleExcelList,
    (state, { options }) =>
      ({
        ...state,
        error: null,
        options: options,
        isLoading: true,
      } as VehiclesState)
  ),
  on(VehiclesActionTypes.loadVehicleExcelList, (state, { response }) => {
    return {
      ...state,
      vehicleExcelList: (response as any)?.result?.items
        ? (response as any)?.result?.items
        : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(
    VehiclesActionTypes.getVehicleTableConfig, 
    (state) => {
    return {
      ...state,
      isLoading: true,
    }
  }),
  on(VehiclesActionTypes.loadVehicleTableConfig, (state, { response }) => {
    return {
      ...state,
      vehicleTableConfig: (response as any)?.result?.value?.tableColumnInfo ?? undefined,
    };
  }),
  on(VehiclesActionTypes.saveVehicleTableConfig, (state) => {
    return {
      ...state,
      isLoading: true,
    };
  }),
  on(VehiclesActionTypes.getVehicle, (state) => {
    return {
      ...state,
      isLoading: true,
      created: false,
    };
  }),
  on(VehiclesActionTypes.resetCreation, (state) => {
    return {
      ...state,
      created: true,
    };
  }),
  on(
    VehiclesActionTypes.createVehicle,
    VehiclesActionTypes.updateVehicle,
    (state) => {
      return {
        ...state,
        isLoading: true,
      };
    }
  )
);

export function reducer(state: VehiclesState | undefined, action: Action) {
  return vehiclesReducer(state, action);
}
